import React from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Avatar,
  Paper,
  Chip,
  MenuItem,
  Container,
} from '@mui/material';
import {
  User as PersonIcon,
  Bell as BellIcon,
  Building as BuildingIcon,
  Palette as PaletteIcon,
  Settings as SettingsIcon,
  Shield as ShieldIcon,
  Link as LinkIcon,
  Mail as MailIcon,
  Smartphone as SmartphoneIcon,
} from 'lucide-react';
import { SystemSettings, AppUser } from '../../types';

interface SettingsViewProps {
  settings: SystemSettings;
  setSettings: React.Dispatch<React.SetStateAction<SystemSettings>>;
  settingsActiveTab: string;
  setSettingsActiveTab: (tab: string) => void;
  handleSaveSettings: () => void;
  handleSettingsPhotoUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  appUsers: AppUser[];
  currentUser: AppUser;
  handleAddNewUser: () => void;
  handleEditUser: (user: AppUser) => void;
  handleToggleUserStatus: (user: AppUser) => void;
  handleDeleteUser: (user: AppUser) => void;
  handleResendWelcomeEmail: (user: AppUser) => Promise<void>;
  setAppUsers: (users: AppUser[]) => void;
  defaultUsers: AppUser[];
}

const SettingsView: React.FC<SettingsViewProps> = ({
  settings,
  setSettings,
  settingsActiveTab,
  setSettingsActiveTab,
  handleSaveSettings,
  handleSettingsPhotoUpload,
  appUsers,
  currentUser,
  handleAddNewUser,
  handleEditUser,
  handleToggleUserStatus,
  handleDeleteUser,
  handleResendWelcomeEmail,
  setAppUsers,
  defaultUsers,
}) => {
  const tabs = [
    { id: 'profile', label: 'Profile', icon: <PersonIcon size={18} /> },
    { id: 'notifications', label: 'Notifications', icon: <BellIcon size={18} /> },
    { id: 'company', label: 'Company', icon: <BuildingIcon size={18} /> },
    { id: 'appearance', label: 'Appearance', icon: <PaletteIcon size={18} /> },
    { id: 'system', label: 'System', icon: <SettingsIcon size={18} /> },
    ...(currentUser.role === 'admin' ? [{ id: 'users', label: 'Users', icon: <PersonIcon size={18} /> }] : []),
    { id: 'security', label: 'Security', icon: <ShieldIcon size={18} /> },
    { id: 'integrations', label: 'Integrations', icon: <LinkIcon size={18} /> },
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ color: '#2c2c2c', fontWeight: 'bold' }}>
          System Settings
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Configure your personal profile, notification preferences, and system-wide application settings.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 3 }}>
          <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {tabs.map((tab) => (
                <Button
                  key={tab.id}
                  onClick={() => setSettingsActiveTab(tab.id)}
                  sx={{
                    justifyContent: 'flex-start',
                    py: 1.5,
                    px: 3,
                    borderRadius: 0,
                    color: settingsActiveTab === tab.id ? '#f59f1f' : '#666',
                    bgcolor: settingsActiveTab === tab.id ? 'rgba(245, 159, 31, 0.05)' : 'transparent',
                    borderLeft: settingsActiveTab === tab.id ? '4px solid #f59f1f' : '4px solid transparent',
                    fontWeight: settingsActiveTab === tab.id ? 'bold' : 'normal',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' }
                  }}
                  startIcon={tab.icon}
                >
                  {tab.label}
                </Button>
              ))}
            </Box>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 9 }}>
          <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, border: '1px solid #e0e0e0', borderRadius: 2 }}>
            {settingsActiveTab === 'profile' && (
              <Box>
                <Typography variant="h6" sx={{ color: '#2c2c2c', fontWeight: 'bold', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon size={20} /> User Profile Settings
                </Typography>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth label="First Name" value={settings.userProfile.firstName}
                      onChange={(e) => setSettings(prev => ({ ...prev, userProfile: { ...prev.userProfile, firstName: e.target.value } }))}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth label="Last Name" value={settings.userProfile.lastName}
                      onChange={(e) => setSettings(prev => ({ ...prev, userProfile: { ...prev.userProfile, lastName: e.target.value } }))}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth label="Email" type="email" value={settings.userProfile.email}
                      onChange={(e) => setSettings(prev => ({ ...prev, userProfile: { ...prev.userProfile, email: e.target.value } }))}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth label="Phone" value={settings.userProfile.phone}
                      onChange={(e) => setSettings(prev => ({ ...prev, userProfile: { ...prev.userProfile, phone: e.target.value } }))}
                      sx={{ mb: 2 }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth label="Job Title" value={settings.userProfile.role}
                      onChange={(e) => setSettings(prev => ({ ...prev, userProfile: { ...prev.userProfile, role: e.target.value } }))}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth label="Department" value={settings.userProfile.department}
                      onChange={(e) => setSettings(prev => ({ ...prev, userProfile: { ...prev.userProfile, department: e.target.value } }))}
                      sx={{ mb: 2 }}
                    />
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2 }}>Profile Photo</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ width: 80, height: 80, backgroundColor: '#f59f1f', fontSize: '2rem', fontWeight: 'bold' }} src={settings.userProfile.avatar}>
                          {!settings.userProfile.avatar && `${settings.userProfile.firstName[0]}${settings.userProfile.lastName[0]}`}
                        </Avatar>
                        <input type="file" accept="image/*" style={{ display: 'none' }} id="settings-photo-upload" onChange={handleSettingsPhotoUpload} />
                        <label htmlFor="settings-photo-upload">
                          <Button variant="outlined" component="span" sx={{ borderColor: '#f59f1f', color: '#f59f1f' }}>Change Photo</Button>
                        </label>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}

            {settingsActiveTab === 'notifications' && (
              <Box>
                <Typography variant="h6" sx={{ color: '#2c2c2c', fontWeight: 'bold', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BellIcon size={20} /> Notification Settings
                </Typography>
                <Grid container spacing={4}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2 }}>Project Notifications</Typography>
                    {[
                      { key: 'projectUpdates', label: 'Project Status Updates' },
                      { key: 'taskAssignments', label: 'Task Assignments' },
                      { key: 'milestoneAlerts', label: 'Milestone Alerts' },
                      { key: 'budgetAlerts', label: 'Budget Alerts' }
                    ].map((item) => (
                      <Box key={item.key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">{item.label}</Typography>
                        <Button
                          size="small"
                          variant={settings.notifications[item.key as keyof typeof settings.notifications] ? 'contained' : 'outlined'}
                          onClick={() => setSettings(prev => ({
                            ...prev,
                            notifications: { ...prev.notifications, [item.key]: !prev.notifications[item.key as keyof typeof prev.notifications] }
                          }))}
                          sx={{
                            backgroundColor: settings.notifications[item.key as keyof typeof settings.notifications] ? '#4caf50' : 'transparent',
                            borderColor: '#4caf50',
                            color: settings.notifications[item.key as keyof typeof settings.notifications] ? 'white' : '#4caf50',
                            minWidth: '60px'
                          }}
                        >
                          {settings.notifications[item.key as keyof typeof settings.notifications] ? 'ON' : 'OFF'}
                        </Button>
                      </Box>
                    ))}
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2 }}>Team & Safety Notifications</Typography>
                    {[
                      { key: 'teamUpdates', label: 'Team Updates' },
                      { key: 'safetyAlerts', label: 'Safety Alerts' }
                    ].map((item) => (
                      <Box key={item.key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">{item.label}</Typography>
                        <Button
                          size="small"
                          variant={settings.notifications[item.key as keyof typeof settings.notifications] ? 'contained' : 'outlined'}
                          onClick={() => setSettings(prev => ({
                            ...prev,
                            notifications: { ...prev.notifications, [item.key]: !prev.notifications[item.key as keyof typeof prev.notifications] }
                          }))}
                          sx={{
                            backgroundColor: settings.notifications[item.key as keyof typeof settings.notifications] ? '#4caf50' : 'transparent',
                            borderColor: '#4caf50',
                            color: settings.notifications[item.key as keyof typeof settings.notifications] ? 'white' : '#4caf50',
                            minWidth: '60px'
                          }}
                        >
                          {settings.notifications[item.key as keyof typeof settings.notifications] ? 'ON' : 'OFF'}
                        </Button>
                      </Box>
                    ))}
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2 }}>Delivery Methods</Typography>
                      {[
                        { key: 'emailEnabled', label: 'Email Notifications', icon: <MailIcon size={16} /> },
                        { key: 'smsEnabled', label: 'SMS Notifications', icon: <SmartphoneIcon size={16} /> },
                        { key: 'inAppEnabled', label: 'In-App Notifications', icon: <BellIcon size={16} /> }
                      ].map((item) => (
                        <Box key={item.key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>{item.icon}</Box>
                            <Typography variant="body2">{item.label}</Typography>
                          </Box>
                          <Button
                            size="small"
                            variant={settings.notifications[item.key as keyof typeof settings.notifications] ? 'contained' : 'outlined'}
                            onClick={() => setSettings(prev => ({
                              ...prev,
                              notifications: { ...prev.notifications, [item.key]: !prev.notifications[item.key as keyof typeof prev.notifications] }
                            }))}
                            sx={{
                              backgroundColor: settings.notifications[item.key as keyof typeof settings.notifications] ? '#2196f3' : 'transparent',
                              borderColor: '#2196f3',
                              color: settings.notifications[item.key as keyof typeof settings.notifications] ? 'white' : '#2196f3',
                              minWidth: '60px'
                            }}
                          >
                            {settings.notifications[item.key as keyof typeof settings.notifications] ? 'ON' : 'OFF'}
                          </Button>
                        </Box>
                      ))}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}

            {settingsActiveTab === 'company' && (
              <Box>
                <Typography variant="h6" sx={{ color: '#2c2c2c', fontWeight: 'bold', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BuildingIcon size={20} /> Company Information
                </Typography>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth label="Company Name" value={settings.company.name}
                      onChange={(e) => setSettings(prev => ({ ...prev, company: { ...prev.company, name: e.target.value } }))}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth label="Address" multiline rows={3} value={settings.company.address}
                      onChange={(e) => setSettings(prev => ({ ...prev, company: { ...prev.company, address: e.target.value } }))}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth label="Phone Number" value={settings.company.phone}
                      onChange={(e) => setSettings(prev => ({ ...prev, company: { ...prev.company, phone: e.target.value } }))}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth label="Email Address" type="email" value={settings.company.email}
                      onChange={(e) => setSettings(prev => ({ ...prev, company: { ...prev.company, email: e.target.value } }))}
                      sx={{ mb: 2 }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth label="Website" value={settings.company.website}
                      onChange={(e) => setSettings(prev => ({ ...prev, company: { ...prev.company, website: e.target.value } }))}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth label="Registration Number" value={settings.company.registrationNumber}
                      onChange={(e) => setSettings(prev => ({ ...prev, company: { ...prev.company, registrationNumber: e.target.value } }))}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth label="Tax Number" value={settings.company.taxNumber}
                      onChange={(e) => setSettings(prev => ({ ...prev, company: { ...prev.company, taxNumber: e.target.value } }))}
                      sx={{ mb: 2 }}
                    />
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2 }}>Company Logo</Typography>
                      <Box sx={{ border: '2px dashed #e0e0e0', borderRadius: 2, p: 3, textAlign: 'center', backgroundColor: '#fafafa' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                          <BuildingIcon size={32} color="#666" />
                          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>Current Logo</Typography>
                        </Box>
                        <Button variant="outlined" sx={{ borderColor: '#f59f1f', color: '#f59f1f' }}>Upload New Logo</Button>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}

            {settingsActiveTab === 'appearance' && (
              <Box>
                <Typography variant="h6" sx={{ color: '#2c2c2c', fontWeight: 'bold', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PaletteIcon size={20} /> Appearance & Localization
                </Typography>
                <Grid container spacing={4}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2 }}>Theme & Colors</Typography>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>Theme</Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {['Light', 'Dark', 'Auto'].map((theme) => (
                          <Button
                            key={theme} variant={settings.appearance.theme === theme ? 'contained' : 'outlined'} size="small"
                            onClick={() => setSettings(prev => ({ ...prev, appearance: { ...prev.appearance, theme: theme as 'Light' | 'Dark' | 'Auto' } }))}
                            sx={{ backgroundColor: settings.appearance.theme === theme ? '#f59f1f' : 'transparent', borderColor: '#f59f1f', color: settings.appearance.theme === theme ? 'white' : '#f59f1f' }}
                          >
                            {theme}
                          </Button>
                        ))}
                      </Box>
                    </Box>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>Primary Color</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ width: 40, height: 40, backgroundColor: settings.appearance.primaryColor, border: '2px solid #e0e0e0', borderRadius: 1 }} />
                        <TextField
                          size="small" value={settings.appearance.primaryColor}
                          onChange={(e) => setSettings(prev => ({ ...prev, appearance: { ...prev.appearance, primaryColor: e.target.value } }))}
                          sx={{ width: 120 }}
                        />
                      </Box>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2 }}>Regional Settings</Typography>
                    <TextField
                      fullWidth label="Language" select value={settings.appearance.language}
                      onChange={(e) => setSettings(prev => ({ ...prev, appearance: { ...prev.appearance, language: e.target.value } }))}
                      sx={{ mb: 2 }}
                    >
                      <MenuItem value="English">English</MenuItem>
                      <MenuItem value="Bemba">Bemba</MenuItem>
                      <MenuItem value="Nyanja">Nyanja</MenuItem>
                    </TextField>
                    <TextField
                      fullWidth label="Date Format" select value={settings.appearance.dateFormat}
                      onChange={(e) => setSettings(prev => ({ ...prev, appearance: { ...prev.appearance, dateFormat: e.target.value } }))}
                      sx={{ mb: 2 }}
                    >
                      <MenuItem value="DD/MM/YYYY">DD/MM/YYYY</MenuItem>
                      <MenuItem value="MM/DD/YYYY">MM/DD/YYYY</MenuItem>
                      <MenuItem value="YYYY-MM-DD">YYYY-MM-DD</MenuItem>
                    </TextField>
                    <TextField
                      fullWidth label="Time Format" select value={settings.appearance.timeFormat}
                      onChange={(e) => setSettings(prev => ({ ...prev, appearance: { ...prev.appearance, timeFormat: e.target.value as '12h' | '24h' } }))}
                      sx={{ mb: 2 }}
                    >
                      <MenuItem value="12h">12 Hour (AM/PM)</MenuItem>
                      <MenuItem value="24h">24 Hour</MenuItem>
                    </TextField>
                    <TextField
                      fullWidth label="Currency" select value={settings.appearance.currency}
                      onChange={(e) => setSettings(prev => ({ ...prev, appearance: { ...prev.appearance, currency: e.target.value } }))}
                      sx={{ mb: 2 }}
                    >
                      <MenuItem value="ZMW">ZMW (Zambian Kwacha)</MenuItem>
                      <MenuItem value="USD">USD (US Dollar)</MenuItem>
                      <MenuItem value="EUR">EUR (Euro)</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </Box>
            )}

            {settingsActiveTab === 'system' && (
              <Box>
                <Typography variant="h6" sx={{ color: '#2c2c2c', fontWeight: 'bold', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SettingsIcon size={20} /> System Configuration
                </Typography>
                <Grid container spacing={4}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2 }}>Backup Settings</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2">Automatic Backup</Typography>
                      <Button
                        size="small" variant={settings.system.autoBackup ? 'contained' : 'outlined'}
                        onClick={() => setSettings(prev => ({ ...prev, system: { ...prev.system, autoBackup: !prev.system.autoBackup } }))}
                        sx={{ backgroundColor: settings.system.autoBackup ? '#4caf50' : 'transparent', borderColor: '#4caf50', color: settings.system.autoBackup ? 'white' : '#4caf50' }}
                      >
                        {settings.system.autoBackup ? 'ENABLED' : 'DISABLED'}
                      </Button>
                    </Box>
                    <TextField
                      fullWidth label="Backup Frequency" select value={settings.system.backupFrequency}
                      onChange={(e) => setSettings(prev => ({ ...prev, system: { ...prev.system, backupFrequency: e.target.value as 'Daily' | 'Weekly' | 'Monthly' } }))}
                      sx={{ mb: 2 }}
                    >
                      <MenuItem value="Daily">Daily</MenuItem>
                      <MenuItem value="Weekly">Weekly</MenuItem>
                      <MenuItem value="Monthly">Monthly</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2 }}>Session & File Settings</Typography>
                    <TextField
                      fullWidth label="Session Timeout (minutes)" type="number" value={settings.system.sessionTimeout}
                      onChange={(e) => setSettings(prev => ({ ...prev, system: { ...prev.system, sessionTimeout: parseInt(e.target.value) } }))}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth label="Max File Size (MB)" type="number" value={settings.system.maxFileSize}
                      onChange={(e) => setSettings(prev => ({ ...prev, system: { ...prev.system, maxFileSize: parseInt(e.target.value) } }))}
                      sx={{ mb: 2 }}
                    />
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1 }}>Allowed File Types</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {settings.system.allowedFileTypes.map((type) => (
                          <Chip key={type} label={type} size="small" sx={{ backgroundColor: '#f0f0f0' }} />
                        ))}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}

            {settingsActiveTab === 'users' && currentUser.role === 'admin' && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <PersonIcon size={24} />
                  <Typography variant="h6" sx={{ ml: 1, fontWeight: 'bold', color: '#2c2c2c' }}>User Management</Typography>
                </Box>
                <Box sx={{ mb: 3, p: 2, bgcolor: settings.notifications.emailEnabled ? '#e8f5e8' : '#fff3cd', borderRadius: 1, border: `1px solid ${settings.notifications.emailEnabled ? '#4caf50' : '#ffc107'}` }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MailIcon size={16} />
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      Email Notifications: {settings.notifications.emailEnabled ? '✅ Enabled' : '⚠️ Disabled'}
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                    {settings.notifications.emailEnabled ? 'New users will automatically receive welcome emails.' : 'New users will NOT receive email notifications.'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" sx={{ color: '#2c2c2c', fontWeight: 'bold' }}>System Users</Typography>
                  <Button variant="contained" onClick={handleAddNewUser} sx={{ bgcolor: '#f59f1f', '&:hover': { bgcolor: '#e08f0f' } }}>Add New User</Button>
                </Box>
                <Box>
                  {appUsers.map((user) => (
                    <Box key={user.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, border: '1px solid #e0e0e0', borderRadius: 1, mb: 2, backgroundColor: user.id === currentUser.id ? '#f5f5f5' : 'white' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: '#f59f1f' }}>{user.profile.firstName[0]}{user.profile.lastName[0]}</Avatar>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{user.profile.firstName} {user.profile.lastName}{user.id === currentUser.id && ' (You)'}</Typography>
                          <Typography variant="body2" color="text.secondary">{user.profile.email} • {user.role.charAt(0).toUpperCase() + user.role.slice(1)}</Typography>
                          <Typography variant="body2" color="text.secondary">{user.profile.department} • {user.isActive ? 'Active' : 'Inactive'}</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button size="small" variant="outlined" onClick={() => handleEditUser(user)} sx={{ borderColor: '#f59f1f', color: '#f59f1f' }}>Edit</Button>
                        {user.id !== currentUser.id && (
                          <>
                            <Button size="small" variant="outlined" color={user.isActive ? 'error' : 'success'} onClick={() => handleToggleUserStatus(user)}>{user.isActive ? 'Deactivate' : 'Activate'}</Button>
                            <Button size="small" variant="outlined" onClick={() => handleResendWelcomeEmail(user)} sx={{ borderColor: '#2196f3', color: '#2196f3' }}>📧 Send Email</Button>
                            <Button size="small" variant="outlined" color="error" onClick={() => handleDeleteUser(user)}>Delete</Button>
                          </>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
                <Box sx={{ mt: 3, p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ color: '#2c2c2c' }}>User Statistics</Typography>
                    <Button
                      variant="outlined" size="small" color="error"
                      onClick={() => {
                        if (window.confirm('Reset all users to default profiles?')) {
                          setAppUsers(defaultUsers);
                          localStorage.setItem('app_users', JSON.stringify(defaultUsers));
                          alert('User profiles reset successfully!');
                        }
                      }}
                    >
                      Reset to Defaults
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}

            {(settingsActiveTab === 'security' || settingsActiveTab === 'integrations') && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                  {settingsActiveTab === 'security' ? <ShieldIcon size={48} color="#999" /> : <LinkIcon size={48} color="#999" />}
                </Box>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>Coming Soon</Typography>
                <Typography variant="body2" color="text.secondary">
                  {settingsActiveTab === 'security' ? 'Advanced security settings will be available soon.' : 'System integrations will be available soon.'}
                </Typography>
              </Box>
            )}

            <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e0e0e0', textAlign: 'right' }}>
              <Button variant="contained" sx={{ backgroundColor: '#f59f1f', color: 'white', px: 4, '&:hover': { backgroundColor: '#e08f0f' } }} onClick={handleSaveSettings}>Save Settings</Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default SettingsView;
